// 📁 src/lightversion/IdeaGeneratorLite.jsx

import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import axios from "../utils/axiosInstance";
import { useLite } from "./context/LiteContext";
import { useNavigate } from "react-router-dom";
import LiteDayGate from "./LiteDayGate";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";
import LiteLearningBlock from "./components/LiteLearningBlock"; // ✅ ADDITION


const IdeaGeneratorLite = () => {
  const navigate = useNavigate();

  const {
  liteJourney,
  selectDirection,
  markDayComplete,
  entryDecided,
  loading: liteLoading
  } = useLite();

  const purposeStatement = liteJourney?.purpose?.statement || "";

  const [contextInput, setContextInput] = useState("");
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* =====================================================
     🔒 ENTRY DECISION GUARD
  ===================================================== */
  useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
    navigate("/lite/welcome", { replace: true });
  }

  }, [entryDecided, liteLoading, navigate])

  /* =====================================================
     🔁 DAY ORDER GUARDS
  ===================================================== */
  useEffect(() => {
    const completed = liteJourney?.completedDays || [];

    if (!completed.includes(1)) {
      navigate("/lite/mentor", { replace: true });
      return;
    }

    if (!completed.includes(2)) {
      navigate("/lite/purpose", { replace: true });
      return;
    }

    if (completed.includes(3)) {
      navigate("/lite/direction-confirm", { replace: true });
    }
  }, [liteJourney, navigate]);

  /* ------------------------------------------------
     Explore POSSIBLE DIRECTIONS
  ------------------------------------------------ */
  const handleExplore = async () => {
    setError("");

    if (!purposeStatement) {
      navigate("/lite/purpose");
      return;
    }

    if (!contextInput.trim()) {
      setError("Pause for a moment and add one reflection before continuing.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post("/lite/ideas", {
        purpose: purposeStatement,
        reflection: contextInput,
        mode: "directional-exploration",
      });

      setDirections(res.data.directions || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "We couldn’t explore directions right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <LiteDayGate allow={["SOLUTION_IDEA", "IDEA_SHAPING"]}>
      <LiteDayLayout day={3}>
        <Box sx={{ maxWidth: 1000, mx: "auto" }}>
          {/* HEADER */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
              Explore Idea Directions
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              Today is about exploration — not choosing a final idea.
            </Typography>
          </motion.div>

          {/* 🔹 DAY 3 INTRO */}
          <Box sx={{ mb: 6 }}>
            <Stack spacing={4}>
              <ExperienceItem
                icon="🧭"
                title="Explore multiple directions"
                text="Purpose is the anchor, but solutions are many.You’ll test different paths without locking in.Exploration builds clarity before commitment."
              />
              <ExperienceItem
                icon="🧠"
                title="Avoid early fixation"
                text="The first idea is rarely the best.Strong innovators explore range before precision.Today is about possibilities, not premature commitment."
              />
              <ExperienceItem
                icon="🌱"
                title="Prepare for informed choice"
                text="Founders don’t rush decisions—they prepare them.By today’s end, you’ll have promising paths.Tomorrow is about calm evaluation, not guesswork."
              />
            </Stack>
          </Box>

          {/* 🧠 OPTIONAL LITE LEARNING (DAY 4 – VALIDATION REALITY) */}
          <LiteLearningBlock day={4} />

          {/* 🔹 PURPOSE CONTEXT */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="subtitle2">
                  Your purpose (from Day 2)
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: "italic",
                    color: purposeStatement
                      ? "text.secondary"
                      : "error.main",
                  }}
                >
                  “{purposeStatement || "Not set yet"}”
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  Add one more reflection to help the AI explore better
                  directions.
                </Typography>

                <TextField
                  label="One more reflection"
                  placeholder="What feels most challenging, unresolved, or confusing about this problem right now?"
                  multiline
                  minRows={3}
                  fullWidth
                  value={contextInput}
                  onChange={(e) => setContextInput(e.target.value)}
                  disabled={!purposeStatement}
                />

                {error && (
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                )}

                <Button
                  variant="contained"
                  size="large"
                  disabled={loading || !purposeStatement}
                  onClick={handleExplore}
                >
                  {loading ? "Exploring…" : "Explore Possible Directions"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* 🔹 DIRECTION RESULTS */}
          {directions.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight="600" sx={{ mb: 2 }}>
                Possible Directions
              </Typography>

              <Grid container spacing={2}>
                {directions.map((dir, idx) => (
                  <Grid item xs={12} md={6} key={idx}>
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {dir.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, mb: 2 }}
                          >
                            {dir.summary}
                          </Typography>

                          {dir.angleTags && (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {dir.angleTags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" />
                              ))}
                            </Stack>
                          )}

                          <Button
                            variant="outlined"
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={() => {
                              selectDirection(dir);
                              markDayComplete();
                              navigate("/lite/direction-confirm");
                            }}
                          >
                            Explore this direction →
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      </LiteDayLayout>
    </LiteDayGate>
  );
};

export default IdeaGeneratorLite;
